import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { API_BASE } from "../DataContext";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildBlogListLD, buildBreadcrumbLD } from "../lib/jsonld";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

export default function Blog() {
  usePageMeta(
    "Blog — Piping & Water Storage Insights",
    "Guides, comparisons and practical tips on water tanks, pipes, plumbing and sanitaryware from the BAISHALII team."
  );
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog`).then((r) => r.json()).then(setPosts).catch(() => setPosts([]));
  }, []);

  const blogLd = buildBlogListLD({
    name: "BAISHALII Blog",
    path: "/blog",
    description: "Guides, comparisons and practical tips on water tanks, pipes, plumbing and sanitaryware.",
    posts: (posts || []).slice(0, 20),
  });
  const breadcrumbLd = buildBreadcrumbLD([
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
  ]);

  return (
    <div className="pt-[88px] md:pt-[112px]" data-testid="blog-page">
      <JsonLd blocks={[blogLd, breadcrumbLd]} id="blog-index" />
      <section className="bg-[#0a2a5e] text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
          <nav className="text-xs text-sky-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2 text-sky-500">/</span>
            <span className="text-white">Blog</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black">BAISHALII Blog</h1>
          <p className="text-base md:text-lg text-sky-100 mt-4 max-w-2xl">
            Guides, tips and know-how on pipes, tanks and plumbing — from the BAISHALII team.
          </p>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-16">
          {!posts && <div className="text-center text-slate-400 py-16">Loading articles...</div>}
          {posts && posts.length === 0 && <div className="text-center text-slate-400 py-16">No articles published yet.</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts && posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-shadow overflow-hidden flex flex-col"
              >
                <div className="aspect-[3/2] overflow-hidden bg-slate-100">
                  {p.coverImage && <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {p.tags.map((t) => (
                      <span key={t} className="text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2.5 py-0.5">{t}</span>
                    ))}
                  </div>
                  <h2 className="text-lg font-black text-[#0a2a5e] group-hover:text-sky-700 transition-colors leading-snug">{p.title}</h2>
                  <p className="text-sm text-slate-500 mt-2 line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Calendar className="h-3.5 w-3.5" /> {fmtDate(p.created_at)}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-bold text-sky-600">
                      Read more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
